import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { BarChart3, Database, Layers, CheckCircle2, FileText, Target, Save, ArrowRight, X } from 'lucide-react';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import Spinner from '../components/ui/Spinner';
import { api } from '../lib/api';
import { saveQuickSchema } from '../lib/api/knowledgeGraphDesignApi';
import { extractErrorMessage } from '../lib/apiErrors';
import { useAnalysisStore } from '../stores/analysisStore';
import {
  readDraft,
  writeDraft,
  clearDraft,
  formatRelativeTime,
  type WizardDraft,
} from '../lib/analysisWizardDraft';
import {
  readAnalysisArchive,
  writeAnalysisArchive,
  clearAnalysisArchive,
} from '../lib/analysisArchive';
import Step0DomainSelection from './analysis/Step0DomainSelection';
import Step1DiscoverySummary from './analysis/Step1DiscoverySummary';
import Step2EnterpriseSystemsSelection from './analysis/Step2EnterpriseSystemsSelection';
import Step3DataMapping from './analysis/Step3DataMapping';
import Step4SchemaPreview from './analysis/Step4SchemaPreviewEnhanced';
import Step5ReviewComplete from './analysis/Step5ReviewComplete';

// Step component type
interface StepConfig {
  number: number;
  title: string;
  icon: React.ReactNode;
  description: string;
}

const steps: StepConfig[] = [
  {
    number: 0,
    title: 'Domain Selection',
    icon: <Target className="w-5 h-5" />,
    description: 'Domain & hypotheses',
  },
  {
    number: 1,
    title: 'Analysis Summary',
    icon: <FileText className="w-5 h-5" />,
    description: 'Review analysis findings',
  },
  {
    number: 2,
    title: 'Enterprise Systems',
    icon: <Database className="w-5 h-5" />,
    description: 'Select data sources',
  },
  {
    number: 3,
    title: 'Data Mapping',
    icon: <Layers className="w-5 h-5" />,
    description: 'AI-powered data mapping',
  },
  {
    number: 4,
    title: 'Schema Design',
    icon: <BarChart3 className="w-5 h-5" />,
    description: 'Neo4j schema generation',
  },
  {
    number: 5,
    title: 'Review & Complete',
    icon: <CheckCircle2 className="w-5 h-5" />,
    description: 'Finalize analysis phase',
  },
];

export const AnalysisWizard: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const caseId = searchParams.get('caseId');
  const autoAnalyze = searchParams.get('autoAnalyze') === 'true';
  const viewMode = searchParams.get('viewMode') === 'true';

  // Determine initial step:
  // - viewMode=true (from Design phase): Start at Step 1 (Analysis Summary) to view existing data
  // - Saved draft for this caseId in localStorage: resume at that step
  // - Default (including autoAnalyze=true): Start at Step 0 (Domain Selection)
  //   autoAnalyze only controls auto-triggering the analyze-all call on Step 1,
  //   NOT skipping Domain Selection — users must always go through Step 0 first.
  const getInitialStep = () => {
    if (viewMode) return 1;
    // Per-case saved draft (set every time the user advances or hits Save Draft).
    // Clamp into a valid range in case the step list changes between releases.
    const draft = readDraft(caseId);
    if (draft && draft.step > 0 && draft.step < steps.length) {
      return draft.step;
    }
    return 0;
  };

  const [currentStep, setCurrentStep] = React.useState<number>(getInitialStep());

  // Resume banner — shown briefly when a draft was restored so the user
  // knows where they are AND can dismiss / start over.  Computed once at mount.
  const initialDraftRef = useRef<WizardDraft | null>(readDraft(caseId));
  const [showResumeBanner, setShowResumeBanner] = useState<boolean>(
    !viewMode && initialDraftRef.current !== null && initialDraftRef.current.step > 0
  );

  // Fetch case data if caseId is provided.
  // Key aligned with the rest of the app (CaseDetail/CaseSummary/CaseClientDetails
  // all use ['case', caseId]) so the case object is shared from one cache
  // entry instead of being re-fetched under a second key.
  const { data: caseData, isLoading: loadingCase } = useQuery({
    queryKey: ['case', caseId],
    queryFn: async () => {
      if (!caseId) return null;
      const response = await api.get(`/cases/${caseId}`);
      return response;
    },
    enabled: !!caseId,
  });

  // Get reset function and case scoping from analysis store
  const resetAnalysis = useAnalysisStore((state) => state.resetAnalysis);
  const setStoreCaseId = useAnalysisStore((state) => state.setCaseId);
  const storedCaseId = useAnalysisStore((state) => state.caseId);
  const storedCaseCreatedAt = useAnalysisStore((state) => state.caseCreatedAt);

  // A case is "the same" only when BOTH its id and its creation time match.
  //
  // caseId alone is not unique over time: the backend reuses ids such as
  // "proj_001", so clearing the database and creating a new case yields a
  // different case with the same id. Comparing ids alone read that as
  // "resuming" and kept the previous case's canvases, systems and schema —
  // the route by which an oil-refinery schema appeared inside a
  // consumer-goods case.
  //
  // When either side's createdAt is unknown (state written by an older build,
  // or the case not yet loaded) we fall back to the id-only comparison rather
  // than resetting a legitimate resume on every mount.
  const caseCreatedAt: string | null =
    (caseData as { createdAt?: string } | null | undefined)?.createdAt ?? null;
  const isSameCase =
    storedCaseId === caseId &&
    (!storedCaseCreatedAt || !caseCreatedAt || storedCaseCreatedAt === caseCreatedAt);

  // Scope the persisted analysis state to the case being opened.
  //
  // The Zustand persist key (`analysis-storage`) is global, not per-case, so
  // without scoping the wizard would either:
  //   (a) leak Case A's canvases/mappings into Case B, or
  //   (b) wipe a resumed draft by always-resetting on mount (the previous
  //       broken behavior — every first mount has prevRef=null so the old
  //       `caseId !== prevRef.current` check fired even on resume).
  //
  // The fix: compare the *persisted* caseId (loaded from localStorage by
  // Zustand) against the caseId being opened.  Same → keep the data so the
  // user resumes their draft.  Different (or absent) → reset, then stamp
  // the new caseId so subsequent partialize() writes are correctly tagged.
  // Whether the store currently holds a DIFFERENT case's data. Children must
  // not render against it: effects run after paint, so with scoping done only
  // in useEffect the step components mounted once with the previous case's
  // systems, canvases and schema and displayed them under the new case's name.
  // Two of the five reported bleed instances (Infosys questions and schema
  // shown inside the Apple case) are exactly this one-render window.
  const isStoreStale = !!caseId && !viewMode && !isSameCase;

  useEffect(() => {
    if (!caseId || viewMode) return;
    // Wait for the case to load before deciding: without createdAt the check
    // would fall back to id-only and wrongly "resume" a recreated case.
    if (!caseData) return;
    if (isSameCase) {
      // Resuming the same case — keep canvases, mappings, selected systems.
      // Stamp createdAt so state written by an older build gains the marker.
      if (!storedCaseCreatedAt && caseCreatedAt) {
        setStoreCaseId(caseId, caseCreatedAt);
      }
      return;
    }
    // Switching cases. The Zustand persist key is GLOBAL, so the store holds only one
    // case at a time — resetting it here used to WIPE the outgoing case's systems/
    // canvases/mappings. With the data gone, the deep-landing guard below bounced a
    // resumed draft back to Step 0. So: ARCHIVE the outgoing case's slice under its own
    // key first, then RESTORE the incoming case's slice if we have one.
    if (storedCaseId) {
      const prev = useAnalysisStore.getState();
      writeAnalysisArchive(
        storedCaseId,
        {
          enterpriseSystems: prev.enterpriseSystems,
          customSystems: prev.customSystems,
          selectedSystemIds: prev.selectedSystemIds,
          questionCanvases: prev.questionCanvases,
          activeCanvasId: prev.activeCanvasId,
          dataMappings: prev.dataMappings,
          // Step 4 was missing from both the archive and the restore, so
          // switching cases left the OUTGOING case's schema in the store and
          // resetAnalysis() below was the only thing clearing it — one render
          // too late. This is how another case's schema and entities (Tableau,
          // in the reported Apple case) rendered under the wrong case.
          schemaDesign: prev.schemaDesign,
          schemaPreviews: prev.schemaPreviews,
        },
        storedCaseCreatedAt,
      );
    }
    const archived = readAnalysisArchive(caseId, caseCreatedAt);
    console.log(
      '[analysis-store] switching scope:',
      storedCaseId, '→', caseId,
      archived ? '(restored archive)' : '(fresh)'
    );
    resetAnalysis();
    if (archived) {
      useAnalysisStore.setState(
        archived as Partial<ReturnType<typeof useAnalysisStore.getState>>
      );
    }
    setStoreCaseId(caseId, caseCreatedAt);
  }, [
    caseId,
    caseData,
    isSameCase,
    caseCreatedAt,
    storedCaseCreatedAt,
    resetAnalysis,
    setStoreCaseId,
    viewMode,
  ]);

  // Persist the current step every time it changes so refreshes and tab-closes
  // resume cleanly.  In viewMode we don't persist because that's a read-only
  // inspection from the Design phase and shouldn't pollute the user's resume point.
  useEffect(() => {
    if (!caseId || viewMode) return;
    // Skip persisting Step 0 — that's the same as "no draft", and we don't want
    // accidental tab visits to overwrite a more advanced saved step.
    if (currentStep > 0) {
      writeDraft(caseId, currentStep);
    }
  }, [caseId, currentStep, viewMode]);

  // Verify case phase - allow viewing Analysis from Design phase
  useEffect(() => {
    if (caseData && caseData.currentPhase !== 'Analysis') {
      // Design phase can view Analysis in viewMode
      if (caseData.currentPhase === 'Design' && viewMode) {
        // Allow viewing analysis data from Design phase
        return;
      }
      // Redirect Discovery phase cases to Discovery wizard
      if (caseData.currentPhase === 'Discovery') {
        navigate(`/cases/new-wizard?caseId=${caseId}`);
      }
    }
  }, [caseData, caseId, navigate, viewMode]);

  // ── Completed-phase landing ─────────────────────────────────────────────
  // Analysis is already finished (the case has moved on to Design/beyond), so coming
  // back into this phase should land on the last step with reviewable CONTENT —
  // "Schema Design" — so the user immediately sees the schema they completed.
  //
  // Why Schema Design and not the literally-last step ("Review & Complete"): that step
  // is a PRE-SUBMISSION CHECKLIST, and its checks read the in-memory analysis store,
  // which is empty on a fresh load. For an already-finished phase it therefore renders
  // "Validation Incomplete", 0 Schema Nodes / 0 Data Mappings and a disabled
  // "Cannot Proceed" button — alarming and wrong — and the user still has to click
  // Back to see the schema. Schema Design fetches the saved schema from Neo4j on
  // mount, so it shows the real thing straight away.
  //
  // Deciding this from the case's PHASE (not the saved draft) also makes the two
  // Schema-Design routes agree. Previously the draft alone decided it:
  //   * Quick    → completed via Step 5, which clears the draft → landed on Step 0.
  //   * Advanced → completes inside the KG-Design wizard and navigates away, so Step 5
  //                (and its clearDraft) never runs → resumed a STALE draft.
  // It also repairs cases that already carry a stale draft, with no storage reset.
  // Runs once per case. viewMode keeps its own read-only entry (Step 1).
  // NOTE: once Step 5 writes/invalidates the single ['case', caseId] key on completion, this ref-based landing workaround is redundant and can be removed.
  const completedLandingRef = useRef<string | null>(null);
  useEffect(() => {
    if (!caseId || viewMode || !caseData) return;
    if (caseData.currentPhase === 'Analysis') {
      // Still in progress → normal draft resume.
      //
      // Also claim the ref now. Completing the schema flips the case to
      // 'Design' mid-session (save_completed_schema sets currentPhase), and
      // without this the effect would fire on that refetch and yank the user
      // back to Schema Design just as they advanced to Review & Complete.
      // This landing rule is for RE-ENTERING an already-finished phase, not for
      // one that finishes while you are standing in it.
      completedLandingRef.current = caseId;
      return;
    }
    if (completedLandingRef.current === caseId) return;
    completedLandingRef.current = caseId;
    const schemaStep = steps.findIndex((s) => s.title === 'Schema Design');
    setCurrentStep(schemaStep >= 0 ? schemaStep : steps.length - 2);
    // Not a "resume" — the phase is complete, so the draft banner would be misleading.
    setShowResumeBanner(false);
  }, [caseData, caseId, viewMode]);

  const handleNext = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
      setShowResumeBanner(false);
    }
  };

  const handleBack = () => {
    if (currentStep === 0) {
      // First step of Analysis → back to the PREVIOUS phase (Discovery), not the Dashboard.
      navigate(`/cases/new-wizard?caseId=${caseId}`);
    } else {
      // Every other step (including the read-only viewMode review) steps back one
      // step within the wizard rather than jumping out to the Case Library.
      setCurrentStep(currentStep - 1);
      setShowResumeBanner(false);
    }
  };

  // Explicit "save where I am" action invoked from the Save Draft button.
  //
  // Steps 2 and 3 persist their own content server-side on Next (selected
  // systems, canvases). Step 4's schema did NOT: this handler wrote the
  // localStorage step pointer and toasted success without any network call, so
  // "Draft saved at 'Schema Design'" was reported while the database held zero
  // schema nodes and the Design phase had nothing to read.
  //
  // Now the schema is POSTed to /schema/save and the toast waits for a 2xx.
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  const handleSaveDraft = async () => {
    if (!caseId || isSavingDraft) return;

    const stepLabel = currentStepConfig?.title || `Step ${currentStep}`;
    const schemaDesign = useAnalysisStore.getState().schemaDesign;

    // Only Step 4 owns server-side state that nothing else persists. On other
    // steps the draft really is just the resume pointer, so keep it local.
    const shouldPersistSchema =
      currentStep === 4 && !!schemaDesign && schemaDesign.nodes.length > 0;

    if (!shouldPersistSchema) {
      writeDraft(caseId, currentStep);
      if (currentStep === 4) {
        // Don't claim a schema was saved when there isn't one to save.
        toast.warning('No schema to save yet', {
          description: 'Generate the schema first, then save your draft.',
        });
        return;
      }
      toast.success(`Draft saved at "${stepLabel}"`, {
        description: 'Come back to this case anytime — you’ll resume right here.',
      });
      return;
    }

    setIsSavingDraft(true);
    try {
      const result = await saveQuickSchema(caseId, {
        schema_id: undefined,
        nodes: schemaDesign.nodes,
        relationships: schemaDesign.relationships,
        constraints: schemaDesign.constraints,
        indexes: schemaDesign.indexes,
        cypherDDL: schemaDesign.cypherDDL || '',
      } as any);

      // Only now is the draft real.
      writeDraft(caseId, currentStep);
      toast.success(`Draft saved at "${stepLabel}"`, {
        description: `${schemaDesign.nodes.length} nodes and ${schemaDesign.relationships.length} relationships saved${
          result?.version ? ` (version ${result.version})` : ''
        }.`,
      });
    } catch (err: any) {
      // Surface what the backend actually said rather than a generic failure.
      console.error('Save draft failed:', err);
      toast.error('Could not save draft', { description: extractErrorMessage(err) });
    } finally {
      setIsSavingDraft(false);
    }
  };

  // "Start Over" flow — uses the themed ConfirmDialog instead of window.confirm()
  // so the dialog matches the rest of the app.  Three handlers:
  //   - handleStartOver   → opens the themed dialog
  //   - confirmStartOver  → user clicked "Yes, start over": true reset
  //   - cancelStartOver   → user clicked "Keep my progress": close dialog AND
  //     dismiss the banner.  Previously Cancel did nothing visible, leaving
  //     the banner stuck on screen and confusing the user about whether they
  //     were "starting over" or "continuing".  Now Cancel = "I'll continue
  //     from where I left off, hide the prompt."
  const [showStartOverDialog, setShowStartOverDialog] = useState(false);

  const handleStartOver = () => {
    setShowStartOverDialog(true);
  };

  const confirmStartOver = () => {
    // True reset.  Wipes the localStorage step pointer, the per-case data archive,
    // AND the Zustand analysis store (Step 2 selections, Step 3 canvases, Step 4
    // schema, etc.).
    clearDraft(caseId);
    // Clear BOTH archive keys.
    //
    // Writes use the createdAt-keyed form (`analysis-archive:<id>:<createdAt>`)
    // so a recreated case cannot read its predecessor's slice. This call passed
    // the id alone, which targets only the LEGACY key - so "Start Over" removed
    // nothing for any case written by the current build, and the archive it
    // claimed to wipe was restored on the next mount.
    clearAnalysisArchive(caseId, caseCreatedAt);
    clearAnalysisArchive(caseId);  // legacy id-only entry, if one exists
    resetAnalysis();
    setShowResumeBanner(false);
    setShowStartOverDialog(false);
    setCurrentStep(0);
    toast.success('Cleared analysis progress', {
      description: 'Starting fresh from Step 0.',
    });
  };

  const cancelStartOver = () => {
    // User chose to keep their progress.  Close dialog AND banner so they
    // continue cleanly from the resumed step without further interruption.
    setShowStartOverDialog(false);
    setShowResumeBanner(false);
  };

  const currentStepConfig = steps[currentStep];

  // Show loading spinner while fetching case data
  if (caseId && loadingCase) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-neutral-100 flex items-center justify-center">
        <div className="text-center">
          <Spinner size="lg" />
          <p className="mt-4 text-neutral-600">Loading case data...</p>
        </div>
      </div>
    );
  }

  // Show error if no caseId
  if (!caseId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-neutral-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-neutral-600">No case ID provided</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-neutral-100 py-8">
      {/* Width: cap at 2xl so narrow steps (forms, lists) stay readable,
          but on Step 4 the Mermaid/ReactFlow schema diagram can be ~1500px+
          and was bleeding past the previous max-w-7xl boundary, leaving
          the Save Draft button visually overlapping the chart. */}
      <div className="max-w-screen-2xl mx-auto px-4">
        {/* Header — Save Draft button moved out, now lives inside the step card */}
        <div className="mb-6 text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <BarChart3 className="w-8 h-8 text-amber-600" />
            <h1 className="text-4xl font-bold text-neutral-900">
              Analysis Phase
            </h1>
          </div>
          <p className="text-lg text-neutral-600">
            Data Mapping & Schema Design for {caseData?.name || 'Case'}
          </p>
        </div>

        {/* Resume banner — shown only on the first render after a draft was restored.
            Tells the user we picked up where they left off and lets them start over. */}
        {showResumeBanner && initialDraftRef.current && (
          <div className="mb-6 flex items-center justify-between gap-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-3">
              <ArrowRight className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-amber-900">
                  Resuming where you left off
                </p>
                <p className="text-xs text-amber-800 mt-0.5">
                  Draft saved {formatRelativeTime(initialDraftRef.current.savedAt)} at{' '}
                  <span className="font-medium">
                    {steps[initialDraftRef.current.step]?.title || `Step ${initialDraftRef.current.step}`}
                  </span>.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Primary action = keep going from the restored step ("Resume");
                  "Start over" stays available as the secondary/destructive option. */}
              <Button variant="primary" size="sm" onClick={() => setShowResumeBanner(false)}>
                Resume
              </Button>
              <Button variant="outline" size="sm" onClick={handleStartOver}>
                Start over
              </Button>
              <button
                onClick={() => setShowResumeBanner(false)}
                className="p-1 text-amber-700 hover:text-amber-900 rounded transition-colors"
                aria-label="Dismiss"
                title="Dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Progress Indicator */}
        <Card className="mb-8 p-6 bg-white shadow-md">
          <div className="relative">
            {/* Progress Line */}
            <div className="absolute top-6 left-0 right-0 h-1 bg-neutral-200">
              <div
                className="h-full bg-amber-500 transition-all duration-500"
                style={{ width: `${((currentStep - 1) / (steps.length - 1)) * 100}%` }}
              />
            </div>

            {/* Steps */}
            <div className="relative flex justify-between">
              {steps.map((step) => {
                const isCompleted = currentStep > step.number;
                const isCurrent = currentStep === step.number;
                const isUpcoming = currentStep < step.number;

                return (
                  <div
                    key={step.number}
                    className="flex flex-col items-center"
                    style={{ width: `${100 / steps.length}%` }}
                  >
                    {/* Step Circle */}
                    <div
                      className={`
                        relative z-10 flex items-center justify-center w-12 h-12 rounded-full border-2 transition-all
                        ${isCompleted ? 'bg-amber-500 border-amber-500 text-white' : ''}
                        ${isCurrent ? 'bg-white border-amber-500 text-amber-600 shadow-lg ring-4 ring-amber-100' : ''}
                        ${isUpcoming ? 'bg-white border-neutral-300 text-neutral-400' : ''}
                      `}
                    >
                      {isCompleted ? (
                        <CheckCircle2 className="w-6 h-6" />
                      ) : (
                        step.icon
                      )}
                    </div>

                    {/* Step Label */}
                    <div className="mt-3 text-center">
                      <p
                        className={`text-sm font-semibold ${
                          isCurrent ? 'text-amber-600' : isCompleted ? 'text-neutral-700' : 'text-neutral-400'
                        }`}
                      >
                        {step.title}
                      </p>
                      <p
                        className={`text-xs mt-1 ${
                          isCurrent ? 'text-neutral-600' : 'text-neutral-400'
                        }`}
                      >
                        {step.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Card>

        {/* Current Step Content */}
        <div className="mb-8">
          <Card className="p-8 bg-white shadow-md">
            {/* Hold the steps back for the one render in which the store still
                holds the previous case's data. The scoping effect below swaps
                it on the same tick; without this gate the steps paint the
                outgoing case's systems/canvases/schema first. */}
            {isStoreStale && (
              <div className="flex items-center justify-center py-16">
                <div className="text-center">
                  <Spinner className="w-8 h-8 mx-auto text-primary-600" />
                  <p className="mt-3 text-sm text-neutral-600">Loading this case…</p>
                </div>
              </div>
            )}
            {!isStoreStale && currentStep === 0 && caseId && (
              <Step0DomainSelection
                caseId={caseId}
                wizardData={caseData?.wizardData}
                onNext={handleNext}
                onBack={handleBack}
              />
            )}
            {!isStoreStale && currentStep === 1 && caseId && (
              <Step1DiscoverySummary
                caseId={caseId}
                wizardData={caseData?.wizardData}
                onNext={handleNext}
                onBack={handleBack}
              />
            )}
            {!isStoreStale && currentStep === 2 && caseId && (
              <Step2EnterpriseSystemsSelection
                caseId={caseId}
                onNext={handleNext}
                onBack={handleBack}
              />
            )}
            {!isStoreStale && currentStep === 3 && caseId && (
              <Step3DataMapping
                caseId={caseId}
                wizardData={caseData?.wizardData}
                onNext={handleNext}
                onBack={handleBack}
              />
            )}
            {!isStoreStale && currentStep === 4 && (
              <Step4SchemaPreview
                onNext={handleNext}
                onBack={handleBack}
              />
            )}
            {!isStoreStale && currentStep === 5 && (
              <Step5ReviewComplete
                caseData={caseData}
                onBack={handleBack}
              />
            )}

            {/* Save Draft strip — bottom of the step card, right-aligned.
                Lives BELOW the step's own Back/Next buttons (which are inside
                the step component) so it doesn't compete with them.  The
                top border-line gives a subtle visual separation.
                Hidden in viewMode and on Step 5 (which has its own terminal
                Complete-or-Cancel mutation that supersedes drafts). */}
            {!viewMode && currentStep < 5 && (
              <div className="mt-8 pt-4 border-t border-neutral-100 flex justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSaveDraft}
                  disabled={isSavingDraft}
                  className="flex items-center gap-2"
                  title="Save your progress and resume later"
                >
                  <Save className="w-4 h-4" />
                  {isSavingDraft ? 'Saving…' : 'Save Draft'}
                </Button>
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Themed Start-Over confirmation — replaces the native window.confirm()
          so the dialog matches the rest of the app. */}
      <ConfirmDialog
        isOpen={showStartOverDialog}
        title="Start the Analysis Phase over from scratch?"
        message={[
          'This will clear all your selected enterprise systems, data mappings, and schema design for this case.',
          'Already-saved data in the backend will be overridden when you reach Review & Complete.',
        ]}
        confirmLabel="Yes, start over"
        cancelLabel="Keep my progress"
        variant="danger"
        onConfirm={confirmStartOver}
        onCancel={cancelStartOver}
      />
    </div>
  );
};

export default AnalysisWizard;
